import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';
import {
  AppBar,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Hidden,
  Toolbar,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Send as SendIcon,
  Warning as WarningIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon
} from '@material-ui/icons';

import logo from '../../assets/logo';
import { FetchError } from '../../common/api/api';
import { formatDate } from '../../common/formatter';
import AppSnackbar from '../../common/AppSnackbar';
import CardTitle from '../../common/card/CardTitle';
import CardContent from '../../common/card/CardContent';
import ConfirmDialog from '../../common/dialogs/ConfirmDialog';
import VerticalStep from '../../common/vertical-stepper/VerticalStep';
import VerticalStepper from '../../common/vertical-stepper/VerticalStepper';
import ErrorView, { StyledCenterView } from '../../common/views/ErrorView';
import SupplierDetailsCard from './cards/SupplierDetailsCard';
import SupplierQualitySystemCard from './cards/SupplierQualitySystemCard';
import SupplierQualityManualCard from './cards/SupplierQualityManualCard';
import SupplierFileAttachmentCard from './cards/SupplierFileAttachmentCard';
import SupplierQualityControlCard from './cards/SupplierQualityControlCard';
import SupplierQualityProgramCard from './cards/SupplierQualityProgramCard';
import { Supplier, BaseSupplierCardProps } from './Supplier.interface';
import {
  useFindSupplierByToken,
  useCompleteSupplierSurvey
} from './Supplier.hooks';

interface Section {
  name: string;
}

const sections: Section[] = [
  { name: 'Details' },
  { name: 'Quality Program' },
  { name: 'Quality Manual' },
  { name: 'Product Verification and Quality Control' },
  { name: 'Quality System Functions' }
];

const StyledContainer = styled.main`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
  z-index: 0;
`;

const StyledBody = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  overflow: hidden;
`;

const StyledContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  overflow: auto;
  flex-flow: row wrap;
  align-items: center;
  justify-content: center;
  background-color: #f1f3f3;
  .card-container {
    width: 100%;
    height: 100%;
    padding: 30px;
    .grid-center {
      &::before,
      &::after {
        content: '';
        flex: 1;
      }
    }
    .grid-actions {
      display: flex;
      padding-bottom: 30px;
      .spacer {
        flex: 1;
        display: flex;
        align-items: center;
        .text {
          margin-left: 16px;
          margin-right: 16px;
          line-height: unset;
        }
      }
    }

    .card-title {
      font-size: 24px;
      padding-top: 32px;
      font-weight: unset;
      line-height: unset;
      text-transform: unset;
      .MuiSvgIcon-root {
        margin-left: 10px;
        margin-right: 16px;
      }
    }
  }
`;

const SupplierSurveyPage: React.FC = () => {
  const [token] = useQueryParam('token', withDefault(StringParam, ''));
  const [selectedStepIndex, setSelectedStepIndex] = useQueryParam(
    'step',
    withDefault(NumberParam, 0)
  );
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [supplier, setSupplier] = useState<Supplier | undefined>();
  const [error, setError] = useState<FetchError | undefined>();
  const [didSubmit, setDidSubmit] = useState<boolean>(false);
  const {
    supplier: data,
    isLoading,
    loadingError,
    findSupplier
  } = useFindSupplierByToken(token);
  const { completedSupplier, isCompleting, completeError, completeSupplier } =
    useCompleteSupplierSurvey(supplier?.id || 0, token);
  const invalidToken: boolean = loadingError?.statusCode === 401;
  const apiError: FetchError | undefined = loadingError || completeError;

  const supplierCardProps: BaseSupplierCardProps = {
    token,
    supplier: supplier!,
    onChange: (newSupplier: Partial<Supplier>) => {
      supplier && setSupplier({ ...supplier, ...newSupplier });
    },
    onError: (error: FetchError) => setError(error)
  };

  const cardComponent = useMemo(() => {
    switch (selectedStepIndex) {
      case 0:
        return <SupplierDetailsCard {...supplierCardProps} />;
      case 1:
        return <SupplierQualityProgramCard {...supplierCardProps} />;
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SupplierQualityManualCard {...supplierCardProps} />
            </Grid>
            <Grid item xs={12}>
              <SupplierFileAttachmentCard {...supplierCardProps} />
            </Grid>
          </Grid>
        );
      case 3:
        return <SupplierQualityControlCard {...supplierCardProps} />;
      case 4:
        return <SupplierQualitySystemCard {...supplierCardProps} />;
      case 5:
        return (
          <Card>
            <CardTitle>
              <AssignmentTurnedInIcon />
              Survey Completed
            </CardTitle>
            <CardContent padding='medium'>
              <Box mt={1} mb={2}>
                <Typography variant='body1'>
                  Thank you for taking the time to accomplish this survey. You
                  may now close this window.
                </Typography>
              </Box>
              <Alert severity='success'>
                <span>
                  Supplier survey sucessfully accomplished on{' '}
                  <strong>{formatDate(supplier?.completed)}</strong>.
                </span>
              </Alert>
            </CardContent>
          </Card>
        );
    }
  }, [supplier, supplierCardProps, selectedStepIndex]);

  // scroll to top on index change
  useLayoutEffect(() => {
    contentRef.current?.scrollTo(0, 0);
  }, [selectedStepIndex]);

  useEffect(() => {
    // only change if supplier exists
    if (!supplier) {
      return;
    }
    // -1 index if not completed
    const lastIndex: number = sections.length - +!supplier.completed;
    if (selectedStepIndex < 0 || selectedStepIndex > lastIndex) {
      setSelectedStepIndex(0);
    }
  }, [supplier, selectedStepIndex, setSelectedStepIndex]);

  // verify token and get supplier
  useEffect(() => {
    findSupplier();
  }, [findSupplier]);

  useEffect(() => {
    if (data) {
      setSupplier(data);
    }
  }, [data]);

  useEffect(() => {
    if (completedSupplier) {
      supplierCardProps.onChange?.(completedSupplier);
      setDidSubmit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedSupplier]);

  useEffect(() => {
    if (supplier?.completed && didSubmit) {
      setSelectedStepIndex(5);
      setDidSubmit(false);
    }
  }, [supplier, didSubmit, setSelectedStepIndex]);

  useEffect(() => {
    setError(apiError);
  }, [apiError]);

  return (
    <StyledContainer>
      <AppBar square color='secondary' position='static'>
        <Toolbar>
          <div className='content'>
            <Typography variant='h6' noWrap className='logo-container'>
              <img
                src={logo}
                className='logo'
                alt='Sierra Labs QMS'
                width={180}
              />
            </Typography>
          </div>
        </Toolbar>
      </AppBar>

      {!supplier && !isLoading && loadingError && (
        <ErrorView
          title={invalidToken ? 'Token has expired' : 'Supplier Not Found'}
          description={
            invalidToken
              ? loadingError.message
              : 'Supplier is either not available or does not exist.'
          }
          icon={<WarningIcon color='disabled' fontSize='inherit' />}
        />
      )}
      {!supplier && isLoading && !loadingError && (
        <StyledCenterView>
          <div className='content'>
            <CircularProgress color='primary' size={64} />
          </div>
        </StyledCenterView>
      )}

      {supplier && (
        <StyledBody>
          <Hidden xsDown>
            <VerticalStepper>
              {sections.map((step, index) => (
                <VerticalStep
                  key={index}
                  label={step.name}
                  step={index + 1}
                  completed={index < selectedStepIndex}
                  selected={index === selectedStepIndex}
                  onClick={() => setSelectedStepIndex(index)}
                >
                  <div className='step-index'>
                    <span className='step-number'>{index + 1}</span>
                  </div>
                  <div className='step-title'>{step.name}</div>
                </VerticalStep>
              ))}
              <VerticalStep
                label='Completed'
                step={6}
                completed={selectedStepIndex === 5}
                selected={selectedStepIndex === 5}
                onClick={() => supplier?.completed && setSelectedStepIndex(5)}
              >
                <div className='step-index'>
                  <span className='step-number'>6</span>
                </div>
                <div className='step-title'>Completed</div>
              </VerticalStep>
            </VerticalStepper>
          </Hidden>

          <StyledContent ref={contentRef}>
            <div className='card-container'>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Grid container spacing={3} className='grid-center'>
                    <Grid item xs={12} md={9} lg={7}>
                      {cardComponent}
                    </Grid>
                  </Grid>
                </Grid>
                {error && (
                  <Grid item xs={12}>
                    <Grid container spacing={3} className='grid-center'>
                      <Grid item xs={12} md={9} lg={7}>
                        <Alert severity='error'>{error.message}</Alert>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Grid container spacing={3} className='grid-center'>
                    <GridActions
                      supplier={supplier}
                      isSubmitting={isCompleting}
                      selectedStepIndex={selectedStepIndex}
                      setSelectedStepIndex={setSelectedStepIndex}
                      onSubmit={() => completeSupplier()}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </StyledContent>
        </StyledBody>
      )}

      <AppSnackbar severity='error' open={!!error}>
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didSubmit}
        onClose={() => didSubmit && setDidSubmit(false)}
      >
        Supplier details updated.
      </AppSnackbar>
    </StyledContainer>
  );
};

interface GridActionsProps {
  supplier: Supplier;
  selectedStepIndex: number;
  isSubmitting?: boolean;
  setSelectedStepIndex: (selectedStepIndex: number) => void;
  onSubmit: () => void;
}

const GridActions: React.FC<GridActionsProps> = (props: GridActionsProps) => {
  const {
    supplier,
    isSubmitting,
    selectedStepIndex,
    setSelectedStepIndex,
    onSubmit
  } = props;
  const [text, setText] = useState<string>('');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const now: Date = useMemo(() => new Date(), []);

  useEffect(() => {
    if (+(supplier.modified || 0) >= +now) {
      setText('Supplier details saved.');
    }
  }, [now, supplier]);

  return (
    <Grid item xs={12} md={9} lg={7} className='grid-actions'>
      {selectedStepIndex > 0 && (
        <Button
          color='secondary'
          variant='contained'
          startIcon={<ChevronLeftIcon />}
          onClick={() => setSelectedStepIndex(selectedStepIndex - 1)}
        >
          Back
        </Button>
      )}
      <div className='spacer'>
        {text && (
          <Typography className='text' variant='body1'>
            {text}
          </Typography>
        )}
      </div>
      {selectedStepIndex < sections.length - 1 && (
        <Button
          color='primary'
          variant='contained'
          endIcon={<ChevronRightIcon />}
          onClick={() => setSelectedStepIndex(selectedStepIndex + 1)}
        >
          Next
        </Button>
      )}
      {selectedStepIndex === sections.length - 1 && (
        <Button
          color='primary'
          variant='contained'
          disabled={isSubmitting}
          onClick={() => setOpenDialog(true)}
          endIcon={
            isSubmitting ? (
              <CircularProgress color='primary' size={20} />
            ) : (
              <SendIcon />
            )
          }
        >
          Submit
        </Button>
      )}

      <ConfirmDialog
        open={openDialog}
        title='Submit Survey'
        confirmLabel='Submit'
        message='Click "Submit" to save and accomplish the survey.'
        onCancel={() => setOpenDialog(false)}
        onConfirm={() => {
          setOpenDialog(false);
          onSubmit();
        }}
      />
    </Grid>
  );
};

export default SupplierSurveyPage;
